import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import get from 'lodash.get';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { LuInbox } from 'react-icons/lu';
import { APP_BG } from '@/constant/theme/colors.constant';
import { Text } from '@/components/atoms/AppText';
import type { DataTableProps, SortOrder, SortState } from './types';
import { TableHeader } from './TableHeader';
import { EmptyState } from './EmptyState';
import { SkeletonRows } from './SkeletonRows';
import Pagination from './Pagination';
import MobileCardView from './MobileCardView';
// ─── DataTable ────────────────────────────────────────────────────────────────
/**
 * A fully reusable, generic table component with:
 *  • Column sorting (controlled or uncontrolled)
 *  • Expandable rows with smooth CSS transition
 *  • Loading skeleton
 *  • Dark-mode aware
 *  • Brand colours: themed header and accent
 *
 * @example
 * <DataTable
 *   data={rows}
 *   columns={columns}
 *   onSort={(by, order) => refetch({ orderBy: by, order })}
 *   sortState={{ orderBy: 'createdOn', order: 'desc' }}
 *   renderExpandedRow={(item) => <MyDetail record={item} />}
 *   onRowExpand={(item) => dispatch(fetchDetail(item.id))}
 *   expandedRowDetail={detailFromStore}
 * />
 */
function DataTable<T>({
  data = [],
  columns,
  onSort,
  sortState,
  renderExpandedRow,
  expandedRowDetail,
  expandedRowIndex,
  onExpandChange,
  onRowExpand,
  showExpandColumn,
  loading = false,
  refetching = false,
  skeletonRows = 15,
  caption,
  className = '',
  pagination,
  rowKey,
  rowTestId,
  'data-testid': dataTestId,
  onRowClick,
}: DataTableProps<T>) {
  // ── Expand state: controlled when expandedRowIndex prop is provided ──────
  const isControlled = expandedRowIndex !== undefined;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [internalExpandedIndex, setInternalExpandedIndex] = useState<
    number | null
  >(null);
  const activeExpandedIndex = isControlled
    ? expandedRowIndex
    : internalExpandedIndex;

  // ── Sort state ──────────────────────────────────────────────────────────
  const [internalSort, setInternalSort] = useState<SortState | undefined>(
    sortState,
  );
  useEffect(() => {
    setInternalSort(sortState);
  }, [sortState]);
  const activeSort = sortState ?? internalSort;

  const handleSort = useCallback(
    (accessor: keyof T | string) => {
      const currentOrder =
        activeSort?.orderBy === accessor ? activeSort.order : undefined;
      // Cycle: none → asc → desc → none
      const nextOrder: SortOrder | undefined =
        currentOrder === undefined
          ? 'asc'
          : currentOrder === 'asc'
            ? 'desc'
            : undefined;
      if (onSort) {
        onSort(accessor, nextOrder as SortOrder);
      } else {
        setInternalSort(
          nextOrder
            ? { orderBy: accessor as string, order: nextOrder }
            : undefined,
        );
      }
    },
    [activeSort, onSort],
  );

  const handleExpandToggle = useCallback(
    (index: number, item: T) => {
      const isOpening = activeExpandedIndex !== index;
      const newIndex = isOpening ? index : null;
      if (!isControlled) {
        setInternalExpandedIndex(newIndex);
      }
      if (onExpandChange) {
        onExpandChange(newIndex, isOpening ? item : null);
      }
      // Legacy callback — only fires on open
      if (isOpening && onRowExpand) {
        onRowExpand(item);
      }
    },
    [activeExpandedIndex, isControlled, onExpandChange, onRowExpand],
  );

  const hasExpand = !!renderExpandedRow;
  // Show chevron column by default, but allow opting out (e.g. when an action
  // button in a data column drives the expand)
  const showChevronCol =
    showExpandColumn !== undefined ? showExpandColumn : hasExpand;
  const totalCols = columns.length + (showChevronCol ? 1 : 0);

  return (
    /*
     * Outer wrapper: clips the table, provides the ring + shadow.
     * The inner scroll-container is separate so the fade masks
     * don't scroll with the content.
     */
    <div
      // Mark this scrollable as the preferred footer scroll target so the
      // global footer only becomes visible when the user scrolls the table
      // to its end.
      data-footer-scrollable
      className={[
        'relative flex flex-col min-w-0 h-full overflow-auto',
        className,
      ].join(' ')}
      ref={scrollContainerRef}
    >
      {/* Subtle dim overlay on rows while refetching */}
      {refetching && (
        <div
          aria-hidden="true"
          className="absolute inset-0 z-10 pointer-events-none bg-white/20 dark:bg-black/20 transition-opacity duration-300"
        />
      )}

      {/* ── Desktop / large-screen: classic table (hidden below lg) ──────── */}
      <div className="hidden lg:block">
        <table
          data-testid={dataTestId ?? ""}
          className="w-full border-collapse text-sm min-w-[640px]"
        >
          {caption && <caption className="sr-only">{caption}</caption>}

          <TableHeader<T>
            columns={columns}
            sortState={activeSort}
            onSort={handleSort}
            hasExpandableRows={showChevronCol}
          />

          <tbody
            className={`divide-y divide-gray-100/70 dark:divide-white/[0.045] ${APP_BG.base}`}
          >
            {loading ? (
              <SkeletonRows
                rows={skeletonRows}
                cols={columns.length}
                hasExpand={showChevronCol}
                columnWidths={columns.map((c) => c.width)}
              />
            ) : data.length === 0 ? (
              <EmptyState colSpan={totalCols} />
            ) : (
              data.map((item, index) => {
                const key = rowKey ? rowKey(item, index) : index;
                const isExpanded = activeExpandedIndex === index;

                return (
                  <Fragment key={key}>
                    {/* ── Main row ───────────────────────────────────────── */}
                    <tr
                      onClick={
                        showChevronCol
                          ? () => handleExpandToggle(index, item)
                          : onRowClick
                            ? () => onRowClick(item)
                            : undefined
                      }
                      data-testid={
                        rowTestId ? rowTestId(item, index) : undefined
                      }
                      className={[
                        'group transition-colors duration-150',
                        index % 2 === 1 ? 'bg-slate-50/80 dark:bg-white/[0.02]' : APP_BG.base,
                        'hover:bg-primary/[0.05] dark:hover:bg-primary/[0.09]',
                        isExpanded
                          ? 'bg-primary/[0.06] dark:bg-primary/[0.10]'
                          : '',
                        (showChevronCol || onRowClick) ? 'cursor-pointer select-none' : '',
                      ].join(' ')}
                    >
                      {/* Data cells */}
                      {columns.map((col, ci) => {
                        const rawValue = get(item, col.accessor as string);
                        const content = col.render
                          ? col.render(item, index)
                          : rawValue;

                        return (
                          <td
                            key={ci}
                            onClick={
                              col.accessor === 'action'
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                            className={[
                              'px-4 py-3.5 align-middle text-left',
                              'text-xs font-normal text-gray-700 dark:text-gray-200',
                              col.noTruncate
                                ? 'overflow-visible'
                                : 'overflow-hidden',
                              col.width ?? '',
                              col.cellClassName ?? '',
                            ].join(' ')}
                          >
                            <div
                              className={
                                col.noTruncate
                                  ? 'min-w-0 w-full'
                                  : 'min-w-0 w-full truncate whitespace-nowrap overflow-hidden'
                              }
                            >
                              {content ?? (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                        );
                      })}

                      {/* Expand toggle chevron — right side */}
                      {showChevronCol && (
                        <td className="w-10 px-3 py-3.5 text-center align-middle">
                          <span
                            className={[
                              'inline-flex items-center justify-center w-6 h-6 rounded-full',
                              'transition-all duration-200',
                              'text-gray-400 group-hover:text-primary',
                              isExpanded
                                ? 'bg-primary/10 text-primary rotate-90'
                                : 'bg-gray-100/80 dark:bg-white/[0.06] hover:bg-gray-200/80 dark:hover:bg-white/[0.10]',
                            ].join(' ')}
                          >
                            <ChevronRightIcon
                              className="h-3.5 w-3.5"
                              strokeWidth={2.5}
                            />
                          </span>
                        </td>
                      )}
                    </tr>

                    {/* ── Expanded panel row ─────────────────────────────── */}
                    {hasExpand && isExpanded && (
                      <tr className="expanded-panel">
                        <td colSpan={totalCols} className="p-0">
                          <div
                            className="animate-[expandDown_220ms_cubic-bezier(0.16,1,0.3,1)]"
                            style={{ contain: 'layout' }}
                          >
                            <div
                              className={[
                                'px-6 py-5',
                                APP_BG.surface,
                                'border-b border-gray-200/70 dark:border-gray-700/50',
                              ].join(' ')}
                            >
                              {renderExpandedRow(expandedRowDetail ?? item)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        {/* Bottom spacer — keeps the last row above the fixed pagination pill */}
        <div className="h-14" aria-hidden="true" />
      </div>

      {/* ── Mobile / tablet card view (hidden at lg and above) ──────────── */}
      <MobileCardView<T>
        className="block lg:hidden"
        data={data}
        columns={columns}
        loading={loading}
        skeletonRows={skeletonRows}
        sortState={activeSort}
        onSort={handleSort}
        renderExpandedRow={renderExpandedRow}
        expandedRowDetail={expandedRowDetail}
        activeExpandedIndex={activeExpandedIndex}
        onExpandToggle={handleExpandToggle}
        showChevronCol={showChevronCol}
        rowKey={rowKey}
        rowTestId={rowTestId}
      />

      {/* ── Mobile empty state (single source of truth for mobile) ──────── */}
      {!loading && data.length === 0 && (
        <div
          className={[
            'block lg:hidden py-16 text-center',
            APP_BG.base,
          ].join(' ')}
        >
          <div className="flex flex-col items-center gap-4">
            <div
              className={[
                'flex items-center justify-center w-16 h-16 rounded-2xl shadow-sm',
                'bg-gray-100/80 dark:bg-white/[0.04]',
                'ring-1 ring-gray-200/60 dark:ring-white/[0.06]',
              ].join(' ')}
            >
              <LuInbox className="h-8 w-8 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <Text as="p" size="xl" weight="semibold" color="muted">
                No records found
              </Text>
              <Text as="p" size="base" color="muted" className="opacity-60 max-w-[260px]">
                Try adjusting your filters or date range
              </Text>
            </div>
          </div>
        </div>
      )}

      {pagination ? (
        <Pagination
          currentPage={pagination.state?.page}
          itemPerpage={pagination.state?.limit}
          totalItem={pagination.totalItem}
          rowsPerPageOptions={pagination.rowsPerPageOptions}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
          onPreviousClick={pagination.onPreviousClick}
          onNextClick={pagination.onNextClick}
        />
      ) : null}


      {/* Keyframe: GPU-composited — only opacity + transform, no layout props */}
      <style>{`
                @keyframes expandDown {
                    from { opacity: 0; transform: translateY(-8px) scaleY(0.97); }
                    to   { opacity: 1; transform: translateY(0)    scaleY(1);    }
                }
                @keyframes refetchSlide {
                    0%   { transform: translateX(-120%); }
                    50%  { transform: translateX(120%); }
                    100% { transform: translateX(120%); }
                }
            `}</style>
    </div>
  );
}

export default DataTable;
